import type { TypedMoney, TypedOrder, TypedOrderItem } from "@Resources/js/laravel"

export const ORDER_STATUS = {
  NEW: 0x00,
  AUTHORIZING: 0x10,
  AUTHORIZED: 0x11,
  AUTHORIZATION_EXPIRED: 0x12,
  REVIEW: 0x13,
  PROCESSING: 0x20,
  PROCESSED: 0x21,
  PAID: 0x30,
  CANCELLED: 0x31,
  FAILED: 0x32,
  REFUNDED: 0x33,
} as const

export type OrderStatusValue = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS]

/**
 * An order line as the server describes it.
 */
export type OrderItemData = TypedOrderItem

/**
 * An order as the server describes it, on both the personal and the
 * organization endpoints.
 */
export type OrderData = Omit<TypedOrder, "items"> & { items: OrderItemData[] }

export interface Order extends OrderData {
  totalQuantity: number
  isNew: () => boolean
  isOnReview: () => boolean
  isFinalized: () => boolean
  isFailed: () => boolean
  canRecover: () => boolean
  statusLabel: () => string
  paymentStatusLabel: () => string
  paymentStatusColor: () => string
  amount: () => TypedMoney
}

const STATUS_LABELS: Record<number, string> = {
  [ORDER_STATUS.NEW]: "new",
  [ORDER_STATUS.AUTHORIZING]: "authorizing",
  [ORDER_STATUS.AUTHORIZED]: "authorized",
  [ORDER_STATUS.AUTHORIZATION_EXPIRED]: "authorization expired",
  [ORDER_STATUS.REVIEW]: "fraud review in progress",
  [ORDER_STATUS.PROCESSING]: "processing",
  [ORDER_STATUS.PROCESSED]: "processed",
  [ORDER_STATUS.PAID]: "paid",
  [ORDER_STATUS.CANCELLED]: "payment cancelled",
  [ORDER_STATUS.FAILED]: "payment failed",
  [ORDER_STATUS.REFUNDED]: "refunded",
}

const PAYMENT_STATUS_LABELS: Record<number, string> = {
  [ORDER_STATUS.NEW]: "",
  [ORDER_STATUS.AUTHORIZING]: "authorizing",
  [ORDER_STATUS.AUTHORIZED]: "authorized",
  [ORDER_STATUS.AUTHORIZATION_EXPIRED]: "expired",
  [ORDER_STATUS.REVIEW]: "fraud review",
  [ORDER_STATUS.PROCESSING]: "authorized",
  [ORDER_STATUS.PROCESSED]: "authorized",
  [ORDER_STATUS.PAID]: "paid",
  [ORDER_STATUS.CANCELLED]: "cancelled",
  [ORDER_STATUS.FAILED]: "failed",
  [ORDER_STATUS.REFUNDED]: "refunded",
}

export default function OrderModel(data: OrderData): Order {
  return {
    ...data,

    totalQuantity: data.total_quantity,

    isNew: () => data.status === ORDER_STATUS.NEW,

    isOnReview: () => data.status === ORDER_STATUS.REVIEW,

    isFinalized: () =>
      ([ORDER_STATUS.PAID, ORDER_STATUS.CANCELLED, ORDER_STATUS.REFUNDED] as number[]).includes(
        data.status,
      ),

    isFailed: () =>
      (
        [ORDER_STATUS.AUTHORIZATION_EXPIRED, ORDER_STATUS.CANCELLED, ORDER_STATUS.FAILED] as number[]
      ).includes(data.status),

    canRecover: () =>
      (
        [
          ORDER_STATUS.NEW,
          ORDER_STATUS.AUTHORIZING,
          ORDER_STATUS.AUTHORIZATION_EXPIRED,
          ORDER_STATUS.FAILED,
        ] as number[]
      ).includes(data.status),

    statusLabel: () => STATUS_LABELS[data.status] ?? "",

    paymentStatusLabel: () => PAYMENT_STATUS_LABELS[data.status] ?? "",

    paymentStatusColor: () => {
      const colors: Record<number, string> = {
        [ORDER_STATUS.AUTHORIZING]: "text-secondary",
        [ORDER_STATUS.AUTHORIZED]: "text-warning",
        [ORDER_STATUS.AUTHORIZATION_EXPIRED]: "text-danger",
        [ORDER_STATUS.REVIEW]: "text-warning",
        [ORDER_STATUS.PROCESSING]: "text-warning",
        [ORDER_STATUS.PROCESSED]: "text-warning",
        [ORDER_STATUS.PAID]: "text-success",
        [ORDER_STATUS.CANCELLED]: "text-danger",
        [ORDER_STATUS.FAILED]: "text-danger",
        [ORDER_STATUS.REFUNDED]: "text-info",
      }
      return colors[data.status] ?? ""
    },

    amount: () => data.amount_received ?? data.amount_reserved ?? data.total,
  }
}
